'use client';

import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';

type ValueTone = 'default' | 'warning' | 'error' | 'success';

interface MetricCardProps {
  label: string;
  value: string;
  detail: string;
  valueTone?: ValueTone;
  className?: string;
  compactOnMobile?: boolean;
}

const VALUE_TONE: Record<ValueTone, string> = {
  default: 'text-text-primary',
  success: 'text-success-text',
  warning: 'text-warning-text',
  error: 'text-error-text',
};

function MetricCard({ label, value, detail, valueTone = 'default', className, compactOnMobile = false }: MetricCardProps) {
  return (
    <Card
      variant="flat"
      className={cn(
        'min-w-0 rounded-[var(--radius-2xl)] border-border bg-surface p-4 shadow-[var(--shadow-1)] sm:p-5',
        compactOnMobile && 'px-3 py-3.5 sm:p-5',
        className,
      )}
    >
      <p className={cn(
        'min-w-0 font-mono text-3xs font-semibold tracking-[0.1em] text-text-tertiary uppercase',
        compactOnMobile && 'text-3xs/4 tracking-[0.08em] sm:tracking-[0.1em]',
      )}>
        {label}
      </p>
      <p className={cn(
        'mt-3 min-w-0 font-sans text-2xl font-bold tabular-nums',
        VALUE_TONE[valueTone],
        compactOnMobile && 'mt-2.5 text-xl sm:mt-3 sm:text-2xl',
      )}>
        {value}
      </p>
      <p className={cn(
        'mt-1 min-w-0 text-xs font-medium wrap-break-word text-text-secondary',
        compactOnMobile && 'text-2xs/4 sm:text-xs',
      )}>
        {detail}
      </p>
    </Card>
  );
}

interface Account {
  balance: number;
  currencyCode?: string;
  type: string;
}

interface ItemSlim {
  status?: string;
  consentExpiresAt?: string;
  accounts: Account[];
}

interface Props {
  items: ItemSlim[];
}

export function ConnectionsMetrics({ items }: Props) {
  const formatCurrency = useFormatCurrency();

  const bankCount = items.length;
  const accountCount = items.reduce((s, i) => s + i.accounts.length, 0);
  const totalBalance = items.reduce(
    (s, i) => s + i.accounts.reduce((acc, a) => acc + (a.type === 'CREDIT' ? 0 : a.balance), 0),
    0,
  );

  const now = Date.now();
  const expired = items.filter((i) => i.consentExpiresAt && new Date(i.consentExpiresAt).getTime() < now).length;
  const expiring = items.filter((i) => {
    if (!i.consentExpiresAt) return false;
    const t = new Date(i.consentExpiresAt).getTime();
    return t >= now && t - now < 7 * 24 * 60 * 60 * 1000;
  }).length;
  const errored = items.filter((i) => i.status === 'LOGIN_ERROR').length;
  const attention = expired + errored + expiring;

  let attentionTone: ValueTone = 'default';
  let attentionDetail = 'Tudo em dia';
  if (expired + errored > 0) {
    attentionTone = 'error';
    attentionDetail = `${expired + errored} precisa(m) de ação`;
  } else if (expiring > 0) {
    attentionTone = 'warning';
    attentionDetail = `${expiring} consentimento(s) expirando`;
  }

  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-4">
      <MetricCard
        label="Bancos"
        value={bankCount.toString()}
        detail={bankCount === 1 ? '1 instituição conectada' : `${bankCount} instituições`}
        compactOnMobile
        className="order-1"
      />
      <MetricCard
        label="Contas"
        value={accountCount.toString()}
        detail={accountCount === 1 ? '1 conta sincronizada' : `${accountCount} contas sincronizadas`}
        compactOnMobile
        className="order-2"
      />
      <MetricCard
        label="Saldo agregado"
        value={formatCurrency(totalBalance)}
        detail="Soma de contas e investimentos"
        className="order-4 col-span-3 sm:order-3 sm:col-span-1"
      />
      <MetricCard
        label="Atenção"
        value={attention.toString()}
        detail={attentionDetail}
        valueTone={attentionTone}
        compactOnMobile
        className="order-3 sm:order-4"
      />
    </div>
  );
}
